import moment from "moment"
import { sortFollowUpsNewestFirst } from "../quotationStatusBadge/activityHistoryUtil"
import type { QuotationStatusVisualVariant } from "../quotation/quotationStatusVariant"
import type { IFollowUpItem } from "./quotationListTypes"

export type QuotationListStatusMetaInput = {
    variant: QuotationStatusVisualVariant
    confirmedOn?: string | null
    nextFollowUpDate?: string | null
    lostReason?: string | null
}

export type QuotationListStatusMetaView = {
    lineText: string
    lineClassName: "confirmed" | "followUp" | "followUpDueToday" | "lostReason"
    showLostReasonInfo: boolean
    lostReasonText: string
}

const formatConfirmedOnDate = (value: string): string => {
    const parsed = moment(value)
    if (!parsed.isValid()) return value
    return parsed.format("D MMM, YYYY")
}

const formatNextFollowUpDate = (value: string): string => {
    const parsed = moment(value)
    if (!parsed.isValid()) return value
    return parsed.format("D MMM YYYY")
}

export const resolveQuotationListNextFollowUpDate = (
    followUps: IFollowUpItem[],
    rowLevelDate?: string | null,
): string | null => {
    if (followUps.length > 0) {
        const latestFollowUp = sortFollowUpsNewestFirst(followUps)[0]
        const latestNextDate = String(latestFollowUp?.next_follow_up_date || "").trim()
        if (!latestNextDate || !moment(latestNextDate).isValid()) return null

        return moment(latestNextDate).format("YYYY-MM-DD")
    }

    const rowDate = String(rowLevelDate || "").trim()
    if (rowDate && moment(rowDate).isValid()) {
        return moment(rowDate).format("YYYY-MM-DD")
    }

    return null
}

export const buildQuotationListStatusMetaView = (
    input: QuotationListStatusMetaInput,
): QuotationListStatusMetaView | null => {
    const lostReasonText = String(input.lostReason || "").trim()

    if (input.variant === "confirmed") {
        const confirmedOn = String(input.confirmedOn || "").trim()
        if (!confirmedOn) return null

        return {
            lineText: `On: ${formatConfirmedOnDate(confirmedOn)}`,
            lineClassName: "confirmed",
            showLostReasonInfo: false,
            lostReasonText: "",
        }
    }

    if (input.variant === "follow up") {
        const nextFollowUpDate = String(input.nextFollowUpDate || "").trim()
        if (!nextFollowUpDate) return null

        const parsed = moment(nextFollowUpDate)
        if (!parsed.isValid()) return null

        const isDueToday = parsed.isSame(moment(), "day")
        return {
            lineText: isDueToday
                ? "Next: Due Today"
                : `Next: ${formatNextFollowUpDate(nextFollowUpDate)}`,
            lineClassName: isDueToday ? "followUpDueToday" : "followUp",
            showLostReasonInfo: false,
            lostReasonText: "",
        }
    }

    if (input.variant === "lost" && lostReasonText.length > 0) {
        return {
            lineText: "Lost Reason",
            lineClassName: "lostReason",
            showLostReasonInfo: true,
            lostReasonText,
        }
    }

    return null
}
